import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface Breadcrumb {
  label: string;
  href?: string;
  external?: boolean;
}

interface BlogSectionHeaderProps {
  title?: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  compact?: boolean;
}

export default function BlogSectionHeader({
  title = 'BrandIndex AI Blog',
  subtitle = 'Learn how to optimize your brand visibility across AI assistants like ChatGPT, Claude, and Gemini.',
  breadcrumbs,
  compact = false,
}: BlogSectionHeaderProps) {
  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${compact ? 'py-6' : 'py-12'}`}>
        {/* Breadcrumbs - Always at top */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className={compact ? 'mb-3' : 'mb-6'} aria-label="Breadcrumb">
            <ol className="flex items-center flex-wrap gap-1 text-sm text-indigo-200">
              {breadcrumbs.map((crumb, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 mx-1 text-indigo-300 flex-shrink-0" />
                  )}
                  {crumb.href ? (
                    crumb.external ? (
                      <a
                        href={crumb.href}
                        className="hover:text-white transition-colors flex items-center gap-1"
                      >
                        {index === 0 && crumb.label === 'Home' && (
                          <Home className="w-4 h-4" />
                        )}
                        <span>{crumb.label}</span>
                      </a>
                    ) : (
                      <Link
                        to={crumb.href}
                        className="hover:text-white transition-colors flex items-center gap-1"
                      >
                        {index === 0 && crumb.label === 'Home' && (
                          <Home className="w-4 h-4" />
                        )}
                        <span>{crumb.label}</span>
                      </Link>
                    )
                  ) : (
                    <span className="text-white font-medium truncate max-w-[200px] sm:max-w-xs">
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Title and Subtitle */}
        <div className={compact ? 'max-w-3xl' : 'text-center max-w-3xl mx-auto'}>
          <h1 className={`font-bold ${compact ? 'text-2xl sm:text-3xl mb-1' : 'text-3xl sm:text-4xl mb-3'}`}>
            {title}
          </h1>
          {subtitle && !compact && (
            <p className="text-lg text-indigo-100">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}
